<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { toIntlLocale } from 'shared/helpers/intlLocale';

const props = defineProps({
  data: { type: Object, required: true },
  accountId: { type: [Number, String], required: true },
  periodCode: { type: String, default: '' },
});

const { t, locale } = useI18n();
const periodLabels = computed(() => ({
  today: t('AI_AGENTS.PROPERTY_FOLLOWUPS.PERIODS.TODAY'),
  '7d': t('AI_AGENTS.PROPERTY_FOLLOWUPS.PERIODS.LAST_7_DAYS'),
  '30d': t('AI_AGENTS.PROPERTY_FOLLOWUPS.PERIODS.LAST_30_DAYS'),
  all: t('AI_AGENTS.PROPERTY_FOLLOWUPS.PERIODS.ALL'),
}));
const periodLabel = computed(
  () =>
    periodLabels.value[props.periodCode] ||
    t('AI_AGENTS.PROPERTY_FOLLOWUPS.PERIOD_DEFAULT')
);

const recentItems = computed(() =>
  Array.isArray(props.data.recent) ? props.data.recent.slice(0, 10) : []
);

const counters = computed(() => [
  {
    label: t('AI_AGENTS.PROPERTY_FOLLOWUPS.COUNTERS.PREPARED'),
    value: props.data.prepared || 0,
    className: 'text-n-slate-12',
  },
  {
    label: t('AI_AGENTS.PROPERTY_FOLLOWUPS.COUNTERS.SENT'),
    value: props.data.sent || 0,
    className: 'text-n-teal-11',
  },
  {
    label: t('AI_AGENTS.PROPERTY_FOLLOWUPS.COUNTERS.PROPERTIES'),
    value: props.data.properties_sent || 0,
    className: 'text-n-teal-11',
  },
  {
    label: t('AI_AGENTS.PROPERTY_FOLLOWUPS.COUNTERS.LEADS'),
    value: props.data.leads || 0,
    className: 'text-n-amber-11',
  },
  {
    label: t('AI_AGENTS.PROPERTY_FOLLOWUPS.COUNTERS.REPLIED'),
    value: props.data.replied || 0,
    className: 'text-n-blue-11',
  },
]);

const eventLabel = type => {
  if (type === 'generated')
    return t('AI_AGENTS.PROPERTY_FOLLOWUPS.EVENTS.GENERATED');
  if (type === 'sent') return t('AI_AGENTS.PROPERTY_FOLLOWUPS.EVENTS.SENT');
  if (type === 'replied')
    return t('AI_AGENTS.PROPERTY_FOLLOWUPS.EVENTS.REPLIED');
  return t('AI_AGENTS.PROPERTY_FOLLOWUPS.EVENTS.UNKNOWN');
};

const eventClass = type => {
  if (type === 'sent') return 'bg-n-teal-3 text-n-teal-11';
  if (type === 'replied') return 'bg-n-blue-3 text-n-blue-11';
  return 'bg-n-slate-3 text-n-slate-11';
};

const formatDate = value => {
  if (!value) return t('AI_AGENTS.PROPERTY_FOLLOWUPS.NO_DATE');
  return new Date(value).toLocaleString(toIntlLocale(locale.value), {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const conversationRoute = item => ({
  name: 'inbox_conversation',
  params: {
    accountId: props.accountId,
    conversation_id: item.conversation_display_id,
  },
});

const leadName = item =>
  item.contact_name || t('AI_AGENTS.PROPERTY_FOLLOWUPS.LEAD_UNKNOWN');

const conversationLabel = item =>
  t('AI_AGENTS.PROPERTY_FOLLOWUPS.CONVERSATION', {
    name: leadName(item),
    id: item.conversation_display_id,
  });

const eventDetails = item =>
  [
    item.inbox_name || t('AI_AGENTS.PROPERTY_FOLLOWUPS.INBOX_UNKNOWN'),
    item.actor_name,
    formatDate(item.occurred_at),
  ]
    .filter(Boolean)
    .join(' · ');

const propertyLabel = property => {
  if (property.code) return property.code;
  if (property.id)
    return t('AI_AGENTS.PROPERTY_FOLLOWUPS.PROPERTY_WITH_ID', {
      id: property.id,
    });
  return t('AI_AGENTS.PROPERTY_FOLLOWUPS.PROPERTY');
};
</script>

<template>
  <section class="mt-6" data-testid="property-followup-panel">
    <div class="flex flex-wrap items-end justify-between gap-2">
      <div>
        <h4 class="text-sm font-medium text-n-slate-12">
          {{ t('AI_AGENTS.PROPERTY_FOLLOWUPS.TITLE') }}
        </h4>
        <p class="text-xs text-n-slate-10">
          {{
            t('AI_AGENTS.PROPERTY_FOLLOWUPS.DESCRIPTION', {
              period: periodLabel,
            })
          }}
        </p>
      </div>
      <span class="text-[10px] text-n-slate-9">
        {{ t('AI_AGENTS.PROPERTY_FOLLOWUPS.PRIVACY') }}
      </span>
    </div>

    <div class="grid grid-cols-2 md:grid-cols-5 gap-3 mt-3">
      <div
        v-for="counter in counters"
        :key="counter.label"
        class="rounded-2xl border border-n-weak p-3 text-center"
      >
        <p class="text-2xl font-extrabold" :class="counter.className">
          {{ counter.value }}
        </p>
        <p class="text-xs text-n-slate-10">{{ counter.label }}</p>
      </div>
    </div>

    <div class="mt-3 overflow-hidden rounded-2xl border border-n-weak">
      <div
        v-if="!recentItems.length"
        class="px-4 py-6 text-center text-sm text-n-slate-10"
      >
        {{ t('AI_AGENTS.PROPERTY_FOLLOWUPS.EMPTY') }}
      </div>
      <template v-else>
        <div
          v-for="item in recentItems"
          :key="item.id"
          data-testid="property-followup-event"
          class="flex flex-col gap-2 border-b border-n-weak px-4 py-3 last:border-b-0 md:flex-row md:items-center"
        >
          <div class="min-w-0 flex-1">
            <div class="flex flex-wrap items-center gap-2">
              <span
                class="rounded-full px-2 py-0.5 text-[10px] font-medium"
                :class="eventClass(item.event_type)"
              >
                {{ eventLabel(item.event_type) }}
              </span>
              <router-link
                v-if="item.conversation_display_id"
                :to="conversationRoute(item)"
                class="truncate text-sm font-medium text-n-slate-12 hover:underline"
              >
                {{ conversationLabel(item) }}
              </router-link>
              <span v-else class="truncate text-sm font-medium text-n-slate-12">
                {{ leadName(item) }}
              </span>
            </div>
            <p class="mt-1 text-xs text-n-slate-10">
              {{ eventDetails(item) }}
            </p>
          </div>
          <div
            v-if="item.property_refs?.length"
            class="flex max-w-full flex-wrap gap-1 md:max-w-[42%] md:justify-end"
          >
            <a
              v-for="property in item.property_refs"
              :key="property.url"
              :href="property.url"
              target="_blank"
              rel="noopener noreferrer"
              class="rounded-md border border-n-weak px-2 py-1 text-xs text-n-teal-11 hover:bg-n-alpha-2"
            >
              {{ propertyLabel(property) }}
            </a>
          </div>
        </div>
      </template>
    </div>
  </section>
</template>
